import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './style/event.css';
import './style/header.css';
import './style/login.css';
import './style/register.css';
import './style/customer.css';
import './style/customer_profile.css';
import './style/group.css';
import './style/playground.css';
import './style/home.css';
import './style/profile.css';
import './style/account_request.css';
import './style/user_settings.css';
import './style/customer_permissions.css';
import './style/about.css';
import 'simplebar-react/dist/simplebar.min.css';
import './style/not_found.css';
import 'react-quill/dist/quill.bubble.css';
import './style/cookie_consent.css';

import { Suspense, lazy } from 'react';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import { HelmetProvider } from 'react-helmet-async';

import { Route, Routes, Navigate } from 'react-router-dom';
import { useDemoGuard } from './hooks/useDemoGuard';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Toaster } from 'react-hot-toast';
import { useGetCurrentUserQuery } from './services/auth_endpoints';

// Eagerly-loaded layout wrappers (always visible, never lazy)
import { Header } from './components/wrapper/header/root';
import { HomeHeader } from './components/wrapper/header/home_header';
import { RequireUser } from './components/shared/require_user';
import { RequireAdmin } from './components/shared/require_admin';
import { CookieConsentBanner } from './components/shared/cookie_consent_banner';
import { usePageTracking } from './hooks/usePageTracking';


// Lazy-loaded global overlays
const VerificationModal   = lazy(() => import('./components/shared/verification_modal').then(m => ({ default: m.VerificationModal })));
const NewsletterModal      = lazy(() => import('./components/shared/newsletter_modal').then(m => ({ default: m.NewsletterModal })));

// Lazy-loaded page components
const HomePage            = lazy(() => import('./components/home/root').then(m => ({ default: m.HomePage })));
const EventsRoot          = lazy(() => import('./components/events/events_root').then(m => ({ default: m.EventsRoot })));
const EventFormContainer  = lazy(() => import('./components/events/form/event_form_container').then(m => ({ default: m.EventFormContainer })));
const EventDashboard      = lazy(() => import('./components/events/event_dashboard/root'));
const EventPage           = lazy(() => import('./components/events/event_page/root').then(m => ({ default: m.EventPage })));
const ProfileRoot         = lazy(() => import('./components/users/profile/root').then(m => ({ default: m.ProfileRoot })));
const DummyProfileRoot    = lazy(() => import('./components/users/profile/dummy_profile_root').then(m => ({ default: m.DummyProfileRoot })));
const UserSettingsRoot    = lazy(() => import('./components/users/settings/root').then(m => ({ default: m.UserSettingsRoot })));
const Login               = lazy(() => import('./components/users/login').then(m => ({ default: m.Login })));
const Register            = lazy(() => import('./components/users/register').then(m => ({ default: m.Register })));
const ForgotPassword      = lazy(() => import('./components/users/forgot_password').then(m => ({ default: m.ForgotPassword })));
const CustomerForm        = lazy(() => import('./components/customer/new').then(m => ({ default: m.CustomerForm })));
const CustomerRoot        = lazy(() => import('./components/customer/customer_root').then(m => ({ default: m.CustomerRoot })));
const CustomerProfile     = lazy(() => import('./components/customer_profile/customer_profile_root').then(m => ({ default: m.CustomerProfile })));
const About               = lazy(() => import('./components/about/root').then(m => ({ default: m.About })));
const Faq                 = lazy(() => import('./components/faq/root').then(m => ({ default: m.Faq })));
const NotFound            = lazy(() => import('./components/shared/not_found').then(m => ({ default: m.NotFound })));
const SlideshowRoute        = lazy(() => import('./components/events/event_page/slideshow/root').then(m => ({ default: m.SlideshowRoute })));
const MultiSlideshowRoot    = lazy(() => import('./components/events/event_page/slideshow/multi_root').then(m => ({ default: m.MultiSlideshowRoot })));
const QrRedirect            = lazy(() => import('./components/qr_redirect').then(m => ({ default: m.QrRedirect })));
const ScoreEntry            = lazy(() => import('./components/score_entry/root').then(m => ({ default: m.ScoreEntry })));
const CampaignRoot          = lazy(() => import('./components/standalone_pages/campaign_root').then(m => ({ default: m.CampaignRoot })));
const CampaignPongifyRoot   = lazy(() => import('./components/standalone_pages/pongify_funnel/pongify_funnel_root').then(m => ({ default: m.PongifyFunnelRoot })));
const DemoRoot              = lazy(() => import('./components/demo/root').then(m => ({ default: m.DemoRoot })));
const HomeCreateEventRoot   = lazy(() => import('./components/home_create_event/root').then(m => ({ default: m.HomeCreateEventRoot })));
const AdminRoot             = lazy(() => import('./components/admin/root').then(m => ({ default: m.AdminRoot })));
const AdminEventStages      = lazy(() => import('./components/admin/pages/event_stages_page').then(m => ({ default: m.EventStagesPage })));
const AdminPlayerStatuses   = lazy(() => import('./components/admin/pages/player_statuses_page').then(m => ({ default: m.PlayerStatusesPage })));
const AdminScoringPerms     = lazy(() => import('./components/admin/pages/event_scoring_permissions_page').then(m => ({ default: m.EventScoringPermissionsPage })));
const AdminEventTypes       = lazy(() => import('./components/admin/pages/event_types_page').then(m => ({ default: m.EventTypesPage })));
const AdminQrCodes          = lazy(() => import('./components/admin/pages/qr_codes_page').then(m => ({ default: m.QrCodesPage })));
const AdminPointCategories  = lazy(() => import('./components/admin/pages/point_categories_page').then(m => ({ default: m.PointCategoriesPage })));
const AdminCustomers        = lazy(() => import('./components/admin/pages/customers_page').then(m => ({ default: m.CustomersPage })));
const AdminEvents           = lazy(() => import('./components/admin/pages/events_page').then(m => ({ default: m.EventsPage })));
const AdminRounds           = lazy(() => import('./components/admin/pages/rounds_page').then(m => ({ default: m.RoundsPage })));
const AdminUsers            = lazy(() => import('./components/admin/pages/users_page').then(m => ({ default: m.UsersPage })));
const AdminDummyUsers       = lazy(() => import('./components/admin/pages/dummy_users_page').then(m => ({ default: m.DummyUsersPage })));
const AdminNewsletter       = lazy(() => import('./components/admin/pages/newsletter_subscriptions_page').then(m => ({ default: m.NewsletterSubscriptionsPage })));
const AdminCampaignLeads    = lazy(() => import('./components/admin/pages/campaign_leads_page').then(m => ({ default: m.CampaignLeadsPage })));
const AdminInvitationConsents = lazy(() => import('./components/admin/pages/invitation_consents_page').then(m => ({ default: m.InvitationConsentsPage })));

// Per-route Suspense wrapper — keeps headers visible while page chunks load
const S = ({ children }) => <Suspense fallback={null}>{children}</Suspense>;

const DemoGuard = () => { useDemoGuard(); return null; };

// Reports a GA4 page view on each route change (no-op until consent is granted).
const PageTracking = () => { usePageTracking(); return null; };

// Bootstraps the session: fires GET /users/me once, then renders immediately
// without blocking on the response. Consumers read the tri-state selectAuthStatus
// (loading / authenticated / anonymous) and resolve their own UI as /me settles,
// so the shell and public content paint without waiting on the round-trip.
const AuthGate = ({ children }) => {
  useGetCurrentUserQuery();
  return children;
};

function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Provider store={store}>
        <HelmetProvider>
          <AuthGate>
          <Suspense fallback={null}>
            <VerificationModal />
            <NewsletterModal />
          </Suspense>
          <DemoGuard />
          <PageTracking />
          <Routes>
            <Route exact path="/" element={<HomeHeader><S><HomePage /></S></HomeHeader>} />
            <Route exact path="/events" element={<Header><S><EventsRoot /></S></Header>} />
            <Route exact path="/customers/:customerId/events/new" element={<Header><S><RequireUser><EventFormContainer /></RequireUser></S></Header>} />
            <Route exact path="/customers/:customerId/events/:eventId" element={<Header><S><RequireUser><EventDashboard/></RequireUser></S></Header>} />
            <Route exact path="/events/:eventId" element={<Header><S><EventPage/></S></Header>} />
            <Route exact path="/tv/:eventId" element={<S><SlideshowRoute/></S>} />
            <Route exact path="/tv/multi/:eventIds" element={<S><MultiSlideshowRoot/></S>} />
            <Route exact path="/users/:userId" element={<Header><S><ProfileRoot/></S></Header>} />
            <Route exact path="/users/:userId/edit" element={<Header><S><RequireUser><UserSettingsRoot/></RequireUser></S></Header>} />
            <Route exact path="/dummy-users/:dummyUserId" element={<Header><S><DummyProfileRoot/></S></Header>} />
            <Route exact path="/login" element={<S><Login/></S>} />
            <Route exact path="/register" element={<S><Register/></S>} />
            <Route exact path="/customers/new" element={<Header><S><RequireUser><CustomerForm/></RequireUser></S></Header>} />
            <Route exact path="/customers/:customerId" element={<Header><S><RequireUser><CustomerRoot/></RequireUser></S></Header>} />
            <Route exact path="/customers/:customerId/public" element={<Header><S><CustomerProfile/></S></Header>} />
            <Route exact path="/about" element={<Header><S><About/></S></Header>} />
            <Route exact path="/faq" element={<Header><S><Faq/></S></Header>} />
            <Route exact path="/forgot-password" element={<S><ForgotPassword/></S>} />
            <Route exact path='/not-found' element={<Header><S><NotFound/></S></Header>} />
            <Route exact path="/qr/:id" element={<S><QrRedirect/></S>} />
            <Route exact path="/score-entry/:token" element={<Header><S><ScoreEntry/></S></Header>} />
            <Route exact path="/campanie-cupe" element={<Header><S><CampaignRoot/></S></Header>} />
            <Route exact path="/campanie-pongify" element={<Header><S><CampaignPongifyRoot/></S></Header>} />
            <Route exact path="/demo" element={<Header><S><DemoRoot/></S></Header>} />
            <Route exact path="/homepage/create_event" element={<Header><S><RequireUser><HomeCreateEventRoot/></RequireUser></S></Header>} />
            <Route exact path="/admin" element={<Header><S><RequireAdmin><AdminRoot/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/event_stages" element={<Header><S><RequireAdmin><AdminEventStages/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/player_statuses" element={<Header><S><RequireAdmin><AdminPlayerStatuses/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/event_scoring_permissions" element={<Header><S><RequireAdmin><AdminScoringPerms/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/event_types" element={<Header><S><RequireAdmin><AdminEventTypes/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/qr_codes" element={<Header><S><RequireAdmin><AdminQrCodes/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/point_categories" element={<Header><S><RequireAdmin><AdminPointCategories/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/customers" element={<Header><S><RequireAdmin><AdminCustomers/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/events" element={<Header><S><RequireAdmin><AdminEvents/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/rounds" element={<Header><S><RequireAdmin><AdminRounds/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/users" element={<Header><S><RequireAdmin><AdminUsers/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/dummy_users" element={<Header><S><RequireAdmin><AdminDummyUsers/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/newsletter_subscriptions" element={<Header><S><RequireAdmin><AdminNewsletter/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/campaign_leads" element={<Header><S><RequireAdmin><AdminCampaignLeads/></RequireAdmin></S></Header>} />
            <Route exact path="/admin/invitation_consents" element={<Header><S><RequireAdmin><AdminInvitationConsents/></RequireAdmin></S></Header>} />
            <Route exact path="/:orgUrl" element={<Header><S><CustomerProfile/></S></Header>} />
            <Route path="*" element={<Navigate to="/not-found" replace />}/>
          </Routes>
          </AuthGate>
        </HelmetProvider>
      </Provider>
      <Toaster position="top-right" reverseOrder={false} />
      <CookieConsentBanner />
    </LocalizationProvider>
  );
}

export default App;
